import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { DE } from "../Languages/DE";
import { EN } from "../Languages/EN";
import { fetchAddressSuggestions } from "../helper/addressSuggest";
import "./LocationSearchInput.css";

const DEBOUNCE_MS = 350;
const MIN_QUERY_LEN = 3;

function LocationSearchInput(props) {
  const [internalAddress, setInternalAddress] = useState(props.defaultValue || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const allowSearchRef = useRef(false);
  const lastDefaultRef = useRef(props.defaultValue);

  const value = props.custom ? props.val || "" : internalAddress;

  const stopSearch = () => {
    allowSearchRef.current = false;
    clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    setSuggestions([]);
    setLoading(false);
  };

  useEffect(() => {
    if (props.custom) return;
    if (props.defaultValue !== lastDefaultRef.current) {
      lastDefaultRef.current = props.defaultValue;
      allowSearchRef.current = false;
      setInternalAddress(props.defaultValue || "");
      setSuggestions([]);
      setLoading(false);
    }
  }, [props.custom, props.defaultValue]);

  useEffect(() => {
    const query = (props.custom ? props.val : internalAddress) || "";
    if (!allowSearchRef.current) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    if (query.trim().length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const results = await fetchAddressSuggestions(query.trim(), {
          signal: controller.signal,
          cityOnly: !!props.isFreeCapacity,
        });
        if (!controller.signal.aborted) {
          setSuggestions(results);
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          setSuggestions([]);
        }
      } finally {
        if (abortRef.current === controller) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [props.custom, props.val, internalAddress, props.isFreeCapacity]);

  const applySelection = (suggestion) => {
    stopSearch();
    const formatted = suggestion.description;

    if (props.custom) {
      props.setVal && props.setVal(formatted);
      props.setFull && props.setFull(formatted);
    } else {
      const street = suggestion.street || formatted.split(",")[0];
      setInternalAddress(props.isFreeCapacity ? suggestion.city || street : street);
      props.setSelectedAddress && props.setSelectedAddress(true);
      if (props.setParam) {
        props.setParam("street", street);
        if (props.isFreeCapacity) {
          props.setParam("city", suggestion.city || street);
        } else if (suggestion.city) {
          props.setParam("city", suggestion.city);
        }
        if (suggestion.lat !== "" && suggestion.lat != null) props.setParam("lat", suggestion.lat);
        if (suggestion.long !== "" && suggestion.long != null) props.setParam("long", suggestion.long);
        if (suggestion.country) props.setParam("country", suggestion.country);
        if (suggestion.postCode) props.setParam("postCode", suggestion.postCode);
        if (suggestion.houseNo) props.setParam("houseNo", suggestion.houseNo);
      }
    }

    props.onAddressSelect && props.onAddressSelect(suggestion);
  };

  const handleChange = (event) => {
    const address = event.target.value;
    allowSearchRef.current = true;
    if (props.custom) {
      props.setVal && props.setVal(address);
      props.setSelectedAddress && props.setSelectedAddress(false);
    } else {
      const add = address.split(",");
      setInternalAddress(add[0]);
      props.setParam && props.setParam("street", add[0]);
      if (props.isFreeCapacity) {
        props.setParam && props.setParam("city", add[0]);
      }
      props.setSelectedAddress && props.setSelectedAddress(false);
    }
  };

  const inputEl = (
    <Form.Control
      value={value}
      onChange={props.disbled ? () => {} : handleChange}
      placeholder={props.placeHolder || (props.isGerm ? DE.Address : EN.Address)}
      className={`location-search-input ${props.isInternal ? "internal-location-input" : ""}`}
      disabled={props.disbled || props.suggestionLoaded}
      autoComplete="new-password"
      type="text"
      onBlur={() => {
        setTimeout(() => {
          setSuggestions([]);
          setLoading(false);
        }, 180);
      }}
    />
  );

  if (props.disbled) {
    return inputEl;
  }

  const showList = suggestions.length > 0;

  return (
    <div className="location-search-wrap location-search-input">
      {inputEl}
      {loading && (
        <span
          aria-hidden="true"
          className={`location-search-input__spinner${props.isInternal ? " location-search-input__spinner--internal" : ""}`}
        />
      )}
      {showList && (
        <div
          className="location-search-dropdown location-search-input__dropdown"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.description}-${index}`}
              onMouseDown={(e) => {
                e.preventDefault();
                applySelection(suggestion);
              }}
              className="location-search-input__option"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
              }}
            >
              <span className="small location-search-input__option-text">
                {suggestion.description}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LocationSearchInput;
